import Tooltip from '@anm/components/Tooltip';
import ButtonLoader from '@anm/components/buttons/ButtonLoader';
import IconButton from '@anm/components/buttons/IconButton';
import NewStreamButton from '@anm/components/stream/NewStreamButton';
import RecorderModal from '@anm/components/stream/RecorderModal';
import RecordingSelectorModal from '@anm/components/stream/RecordingSelectorModal';
import { SECOND_MS } from '@anm/constants/date';
import isMobile from '@anm/helpers/is/isMobile';
import { getLinkTarget } from '@anm/helpers/upgradeLink';
import useSwitchState from '@anm/hooks/useSwitchState';
import { Features } from '@anm/shared/features';
import { FC } from 'react';
import { useSelector } from 'react-redux';

import configs from '../../../../config';
import { getLastSelectedFolderId } from '../../../../hooks/streaming/useSelectFolderPath';
import { userSelectors } from '../../../../store/modules/user';
import { videoFolderSelectors } from '../../../../store/modules/videoFolder';
import useStorageError from '../hooks/useStorageError';

import {
  NewRecordingButtonWrapper,
  OpenStudioButtonWrapper,
  SetupLiveButtonWrapper,
  SetupWebinarButtonWrapper,
  Wrapper
} from './Wrapper';
import { LiveButtonsProps, NewRecordingButtonProps } from './types';
import useWorkspaceParam from './useWorkspaceParam';

const { urls } = configs();

const SetupLiveButton: FC<LiveButtonsProps> = ({ isPending, onOpenNewStreamModal }) => (
  <SetupLiveButtonWrapper>
    <NewStreamButton
      text="Setup Live Stream"
      variant="blue-saturated"
      iconName="broadcast_white"
      onClick={onOpenNewStreamModal}
      tooltipDelayHide={0}
      tooltipDelayShow={SECOND_MS}
      isPending={isPending}
      renderTooltip={
        isMobile()
          ? undefined
          : () => (
              <span>
                Go live or schedule live event to stream <br />
                to one or more destinations
              </span>
            )
      }
    />
  </SetupLiveButtonWrapper>
);
const SetupWebinarButton: FC<LiveButtonsProps> = ({ isPending, onOpenNewStreamModal }) => (
  <SetupWebinarButtonWrapper>
    <NewStreamButton
      text="Setup Webinar"
      variant="blue-saturated"
      iconName="broadcast_white"
      onClick={onOpenNewStreamModal}
      tooltipDelayHide={0}
      tooltipDelayShow={SECOND_MS}
      isPending={isPending}
      renderTooltip={isMobile() ? undefined : () => <span>New stream with webinar</span>}
    />
  </SetupWebinarButtonWrapper>
);

const NewRecordingButton: FC<NewRecordingButtonProps> = ({
  href = `${urls.streamingStudio}new-recording?skipSetup=yes`,
  isPending,
  onClick
}) => (
  <NewRecordingButtonWrapper>
    <Tooltip
      withArrow
      variant="black"
      delayShow={SECOND_MS}
      direction="to-bottom"
      renderContent={
        isMobile()
          ? undefined
          : () => (
              <span>
                Open studio and start recording your <br />
                screen, camera and/or your guests
              </span>
            )
      }
    >
      <ButtonLoader
        size="big"
        color="blue"
        target="_blank"
        variant="white-blue-border-share"
        icon="images/streaming/recording_blue.svg"
        isPending={isPending}
        {...(onClick ? { onClick } : { href })}
      >
        New Recording
      </ButtonLoader>
    </Tooltip>
  </NewRecordingButtonWrapper>
);

const OpenStudioButton: FC = () => {
  const workspaceParam = useWorkspaceParam('?');

  return (
    <OpenStudioButtonWrapper>
      <Tooltip
        withArrow
        variant="black"
        delayShow={SECOND_MS}
        direction="to-bottom"
        renderContent={
          isMobile()
            ? undefined
            : () => (
                <span>
                  Opens live streaming studio in meeting mode. <br />
                  You can explore the studio, edit brands and <br />
                  meet with guests.
                </span>
              )
        }
      >
        <IconButton
          size="big"
          target={getLinkTarget()}
          href={`${urls.streamingStudio}my-room${workspaceParam}`}
          variant="white-blue-border-share"
          icon="images/streaming/camera_blue.svg"
        >
          Open Studio
        </IconButton>
      </Tooltip>
    </OpenStudioButtonWrapper>
  );
};

const NewRecording: FC = () => {
  const [isRecordingOptionsModal, openRecordingOptions, closeRecordingOptions] = useSwitchState();
  const [isRecorderModal, openRecorder, closeRecorder] = useSwitchState();
  const currentFolder = useSelector(videoFolderSelectors.selectCurrentFolder);

  const handelSimpleSelect = () => {
    openRecorder();
    closeRecordingOptions();
  };

  const lastSelectedFolderId = getLastSelectedFolderId();
  const folderParam = lastSelectedFolderId ? `&folder=${lastSelectedFolderId}` : '';
  const workspaceParam = useWorkspaceParam();

  const target = '_self';
  const newRecordingUrl = `${urls.streamingStudio}new-recording?skipSetup=yes${folderParam}${workspaceParam}`;

  const openStudioRecording = () => window.open(newRecordingUrl, target);

  const [fetchStorage, renderStorageModal, isPending] = useStorageError({
    onSuccess: Features.videoRecorderChooser && !isMobile() ? openRecordingOptions : openStudioRecording,
    actionType: 'stream'
  });

  return (
    <>
      {renderStorageModal()}
      {isRecordingOptionsModal && (
        <RecordingSelectorModal
          advancedLink={{
            target,
            href: newRecordingUrl
          }}
          onSimpleSelect={handelSimpleSelect}
          title="Please choose how would you like to record:"
          onModalClose={closeRecordingOptions}
        />
      )}
      {isRecorderModal && (
        <RecorderModal waveUrl={urls.wave} folderId={currentFolder?.id} onModalClose={closeRecorder} />
      )}
      <NewRecordingButton href={newRecordingUrl} isPending={isPending} onClick={fetchStorage} />
    </>
  );
};

const NewStream: FC<LiveButtonsProps> = ({ onOpenNewStreamModal }) => {
  const [fetchStorage, renderStorageModal, isPending] = useStorageError({
    onSuccess: () => onOpenNewStreamModal(false),
    actionType: 'stream'
  });

  return (
    <>
      {renderStorageModal()}
      <SetupLiveButton isPending={isPending} onOpenNewStreamModal={fetchStorage} />
    </>
  );
};

const NewWebinar: FC<LiveButtonsProps> = ({ onOpenNewStreamModal }) => {
  const [fetchStorage, renderStorageModal, isPending] = useStorageError({
    onSuccess: () => onOpenNewStreamModal(true),
    actionType: 'stream'
  });

  return (
    <>
      {renderStorageModal()}
      <SetupWebinarButton isPending={isPending} onOpenNewStreamModal={fetchStorage} />
    </>
  );
};

const LiveButtons: FC<LiveButtonsProps> = props => {
  const isEnabledWebinar = useSelector(userSelectors.selectIsWebinarsEnabled);

  return (
    <Wrapper>
      <NewStream {...props} />
      {isEnabledWebinar && <NewWebinar {...props} />}
      <NewRecording />
      <OpenStudioButton />
    </Wrapper>
  );
};

export default LiveButtons;
